import type { ComposerScene } from '@vellnes/composer-react';

import { RenderGenerationStatus } from '../../../renderGenerations/renderGenerationsTypes';
import { ComposerAspectRatioValue, ComposerUnits } from '../../composerTypes';

// Namespaced key prefix — lets us identify/clear only composer-scene entries
// among everything else the app keeps in localStorage.
const SCENE_KEY_PREFIX = 'composer_scene:';
const REGISTRY_KEY = 'composer_scenes_registry';
const MAX_STORED_SCENES = 50;

interface ComposerSceneRegistryEntry {
  sceneId: string;
  // Monotonic write order, not a timestamp — Date.now() can repeat across
  // saves that happen within the same millisecond (e.g. rapid onSceneChange
  // events), which would make "oldest" ties unstable. A strictly increasing
  // counter keeps LRU ordering deterministic regardless of clock resolution.
  seq: number;
}

type ComposerSceneRegistry = ComposerSceneRegistryEntry[];

// Only the fields needed to restore the sidebar and, for unfinished
// generations, resume polling by serverId. Not the full runtime item shape —
// `kind` is always 'render' and is reconstructed on load.
export interface PersistedComposerRenderGeneration {
  localId: string;
  serverId?: string;
  status: RenderGenerationStatus;
  createdOrder: number;
  resultPreviewUrl?: string;
  resultFullUrl?: string;
  errorMessage?: string;
}

export interface PersistedComposerDisplaySettings {
  showDimensions: boolean;
  units: ComposerUnits;
  aspectRatio: ComposerAspectRatioValue;
  customWidth: number;
  customHeight: number;
  requestedWidth: number;
  requestedHeight: number;
}

export interface ComposerSceneStorageEntry {
  scene: ComposerScene;
  renderGenerations: PersistedComposerRenderGeneration[];
  // Optional — added after the initial storage format shipped. Absent on
  // entries persisted before this field existed (see normalizeStoredEntry).
  displaySettings?: PersistedComposerDisplaySettings;
}

function buildSceneStorageKey(sceneId: string): string {
  return `${SCENE_KEY_PREFIX}${sceneId}`;
}

// djb2 — fast, dependency-free, stable across sessions/browsers. Collisions
// are astronomically unlikely for the cardinality of product-set combinations
// this app deals with, and a collision only means one scene's saved layout
// gets overwritten by another's — never a crash or data corruption elsewhere.
function hashString(value: string): string {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
}

function buildSceneId(productNanoIds: string[]): string {
  const sorted = [...productNanoIds].sort().join(',');
  return hashString(sorted);
}

function isQuotaExceededError(err: unknown): boolean {
  return (
    err instanceof DOMException &&
    (err.name === 'QuotaExceededError' ||
      err.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  );
}

function readRegistry(): ComposerSceneRegistry {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(REGISTRY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('[composerSceneStorage] failed to read registry', error);
    return [];
  }
}

function writeRegistry(registry: ComposerSceneRegistry): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(REGISTRY_KEY, JSON.stringify(registry));
  } catch (error) {
    console.error('[composerSceneStorage] failed to write registry', error);
  }
}

// Drops the oldest-by-seq entries (and their scene payloads) once the
// registry exceeds MAX_STORED_SCENES, so localStorage never accumulates
// unbounded composer-scene data.
function evictOldestScenes(
  registry: ComposerSceneRegistry
): ComposerSceneRegistry {
  if (registry.length <= MAX_STORED_SCENES) return registry;

  const sorted = [...registry].sort((a, b) => b.seq - a.seq);
  const kept = sorted.slice(0, MAX_STORED_SCENES);
  const evicted = sorted.slice(MAX_STORED_SCENES);

  evicted.forEach((entry) => {
    try {
      window.localStorage.removeItem(buildSceneStorageKey(entry.sceneId));
    } catch (error) {
      console.error('[composerSceneStorage] failed to evict scene', error);
    }
  });

  return kept;
}

function nextSeq(registry: ComposerSceneRegistry): number {
  return registry.reduce((max, entry) => Math.max(max, entry.seq), 0) + 1;
}

function touchRegistryEntry(
  registry: ComposerSceneRegistry,
  sceneId: string
): ComposerSceneRegistry {
  const withoutCurrent = registry.filter((entry) => entry.sceneId !== sceneId);
  return [...withoutCurrent, { sceneId, seq: nextSeq(registry) }];
}

function isPersistedRenderGeneration(
  value: unknown
): value is PersistedComposerRenderGeneration {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.localId === 'string' &&
    typeof candidate.status === 'string' &&
    typeof candidate.createdOrder === 'number'
  );
}

// requestedWidth/Height are validated separately (not required) — entries
// persisted before that field existed must still normalize successfully.
function isPersistedDisplaySettingsBase(
  candidate: Record<string, unknown> | undefined
): boolean {
  if (!candidate) return false;
  return (
    typeof candidate.showDimensions === 'boolean' &&
    typeof candidate.units === 'string' &&
    typeof candidate.aspectRatio === 'string' &&
    typeof candidate.customWidth === 'number' &&
    typeof candidate.customHeight === 'number'
  );
}

function normalizeStoredEntry(raw: unknown): ComposerSceneStorageEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const candidate = raw as Record<string, unknown>;

  // Back-compat: earlier versions stored the ComposerScene object directly
  // under the scene key, with no wrapper and no render generations.
  if (!('scene' in candidate) && 'schemaVersion' in candidate) {
    return {
      scene: candidate as unknown as ComposerScene,
      renderGenerations: []
    };
  }

  if (!candidate.scene || typeof candidate.scene !== 'object') return null;

  const renderGenerations = Array.isArray(candidate.renderGenerations)
    ? candidate.renderGenerations.filter(isPersistedRenderGeneration)
    : [];

  const rawDisplaySettings = candidate.displaySettings as
    | Record<string, unknown>
    | undefined;
  const displaySettings = isPersistedDisplaySettingsBase(rawDisplaySettings)
    ? ({
        ...rawDisplaySettings,
        requestedWidth:
          typeof rawDisplaySettings.requestedWidth === 'number'
            ? rawDisplaySettings.requestedWidth
            : rawDisplaySettings.customWidth,
        requestedHeight:
          typeof rawDisplaySettings.requestedHeight === 'number'
            ? rawDisplaySettings.requestedHeight
            : rawDisplaySettings.customHeight
      } as PersistedComposerDisplaySettings)
    : undefined;

  return {
    scene: candidate.scene as ComposerScene,
    renderGenerations,
    ...(displaySettings && { displaySettings })
  };
}

function loadComposerSceneEntry(
  sceneId: string
): ComposerSceneStorageEntry | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(buildSceneStorageKey(sceneId));
    return raw ? normalizeStoredEntry(JSON.parse(raw)) : null;
  } catch (error) {
    console.error('[composerSceneStorage] failed to load scene', error);
    return null;
  }
}

function writeSceneEntry(
  registry: ComposerSceneRegistry,
  sceneId: string,
  entry: ComposerSceneStorageEntry
): void {
  window.localStorage.setItem(
    buildSceneStorageKey(sceneId),
    JSON.stringify(entry)
  );
  writeRegistry(registry);
}

function saveComposerSceneEntry(
  sceneId: string,
  entry: ComposerSceneStorageEntry
): void {
  if (typeof window === 'undefined') return;

  const registry = evictOldestScenes(
    touchRegistryEntry(readRegistry(), sceneId)
  );

  try {
    writeSceneEntry(registry, sceneId, entry);
  } catch (error) {
    if (isQuotaExceededError(error)) {
      // Storage is full — drop the single oldest scene and retry once. If it
      // still doesn't fit, give up silently rather than clearing unrelated
      // app data the way a blanket localStorage.clear() would.
      const sortedByAge = [...registry].sort((a, b) => a.seq - b.seq);
      const oldest = sortedByAge[0];

      if (oldest && oldest.sceneId !== sceneId) {
        try {
          window.localStorage.removeItem(buildSceneStorageKey(oldest.sceneId));
          writeSceneEntry(
            registry.filter((item) => item.sceneId !== oldest.sceneId),
            sceneId,
            entry
          );
          return;
        } catch (retryError) {
          console.error(
            '[composerSceneStorage] quota exceeded, retry failed',
            retryError
          );
          return;
        }
      }

      console.error('[composerSceneStorage] quota exceeded', error);
      return;
    }

    console.error('[composerSceneStorage] failed to save scene', error);
  }
}

function clearAllComposerScenes(): void {
  if (typeof window === 'undefined') return;

  try {
    readRegistry().forEach((entry) => {
      window.localStorage.removeItem(buildSceneStorageKey(entry.sceneId));
    });
    window.localStorage.removeItem(REGISTRY_KEY);
  } catch (error) {
    console.error('[composerSceneStorage] failed to clear scenes', error);
  }
}

export {
  buildSceneId,
  loadComposerSceneEntry,
  saveComposerSceneEntry,
  clearAllComposerScenes,
  MAX_STORED_SCENES
};
